import React from 'react';

import { Button } from '@rbilabs/components-library';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { IUpsellOffer } from '@rbi-ctg/menu';
import Modal, { ModalContent, ModalHeading } from 'components/modal';
import Picture from 'components/picture';
import SanityBlockRenderer, {
  IBlockContentOverrides,
  ISerializers,
  PassthroughComponent,
} from 'components/sanity-block-renderer';

import { useOffersUIContext } from './offers-ui-context';

const UpsellModalHeading = styled(ModalHeading)`
  margin-bottom: 1rem;
`;
const UpsellModalContent = styled(ModalContent)`
  & div.modal-inner {
    display: flex;
    height: 100%;
    justify-content: space-between;
  }
`;
const UpsellOptionsWrapper = styled.div<{ $numColumns: number }>`
  display: grid;
  ${({ $numColumns }) => Styles.desktop`
    grid-gap: 1rem;
    grid-template-columns: repeat(${$numColumns}, 1fr);
  `}
  margin-bottom: 1rem;
`;
const UpsellOption = styled.button<{ $limitWidth: boolean }>`
  // Reset default button styles
  background: transparent;
  border: none;
  text-align: left;

  display: grid;
  grid-template-columns: 140px 1fr;
  grid-gap: 0.5rem;
  align-items: center;
  width: 100vw;
  height: 140px;
  ${({ $limitWidth }) => Styles.desktop`
    grid-template-columns: unset;
    cursor: pointer;
    width: auto;
    height: auto;
    max-height: 260px;
    max-width: ${$limitWidth ? '350px' : 'auto'};
  `}

  padding: 1rem;
  background-color: white;
  border-bottom: 1px solid ${Styles.color.grey.five};
  ${Styles.desktop`
    border-bottom: 0 none;
    border-radius: 10px;
  `}
`;
const TextContent = styled.div`
  height: 85px;
`;

const Title = styled.h3`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;

  margin: 0;
  font-family: ${Styles.fontFamily.base};
  font-weight: normal;
  font-size: 1rem;
`;
const Description = styled.p`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;

  margin: 0.5rem 0 0;
  font-size: 0.9rem;
`;
const ButtonWrapper = styled.div`
  margin-bottom: 2rem;
`;

const titleBlockContentOverrides: IBlockContentOverrides = {
  normal: Title,
};

const titleBlockSerializers: ISerializers = {
  marks: {
    em: PassthroughComponent,
  },
  container: PassthroughComponent,
};

const descriptionBlockContentOverrides: IBlockContentOverrides = {
  normal: Description,
};

const descriptionBlockSerializers: ISerializers = {
  container: PassthroughComponent,
};

interface IOfferUpsellModal {
  onDismiss: () => void;
  parentId: string;
  upsellOptions: Array<IUpsellOffer>;
}
const OfferUpsellModal: React.FC<IOfferUpsellModal> = ({ onDismiss, parentId, upsellOptions }) => {
  const { formatMessage } = useIntl();
  const { onSelectOffer } = useOffersUIContext();

  return (
    <Modal
      data-testid="offer-upsell-modal"
      onDismiss={onDismiss}
      mParticleEventData={{
        modalAppearanceEventMessage: 'Upsell Offer',
        modalMessage: `Parent Offer ID: ${parentId}`,
      }}
    >
      <UpsellModalContent>
        <UpsellModalHeading id="modal-heading">
          {formatMessage({ id: 'offerUpsellTitle' })}
        </UpsellModalHeading>
        <UpsellOptionsWrapper $numColumns={upsellOptions.length > 1 ? 2 : 1}>
          {upsellOptions.map(offer => (
            <UpsellOption
              data-testid={`${offer._id}__offer-upsell`}
              key={offer._id}
              onClick={() => {
                onSelectOffer(offer._id, true);
              }}
              $limitWidth={upsellOptions.length > 1}
            >
              <Picture image={offer.localizedImage?.locale?.app} alt="" objectFitContain={true} />
              <TextContent>
                <SanityBlockRenderer
                  blockContentOverrides={titleBlockContentOverrides}
                  serializers={titleBlockSerializers}
                  content={offer?.name?.localeRaw}
                />
                <SanityBlockRenderer
                  blockContentOverrides={descriptionBlockContentOverrides}
                  serializers={descriptionBlockSerializers}
                  content={offer?.description?.localeRaw}
                />
              </TextContent>
            </UpsellOption>
          ))}
        </UpsellOptionsWrapper>
        <ButtonWrapper>
          <Button data-testid="offer-upsell-discard" onClick={onDismiss}>
            {formatMessage({ id: 'offerUpsellDiscard' })}
          </Button>
        </ButtonWrapper>
      </UpsellModalContent>
    </Modal>
  );
};

export default OfferUpsellModal;
